import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface Game {
  id: number;
  name: string;
  description: string;
  image_url: string | null;
}

interface EditGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: Game | null;
  onSave: () => void;
}

const EditGameModal: React.FC<EditGameModalProps> = ({ isOpen, onClose, game, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getCsrfToken = (): string | null => {
    const tokenEl = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    return tokenEl ? tokenEl.content : null;
  };
  useEffect(() => {
    if (game) {
      setName(game.name);
      setDescription(game.description);
      setImageFile(null); // Clear image file when a new game is selected
    } else {
      setName('');
      setDescription('');
      setImageFile(null);
    }
    setError(null); // Clear errors on game change
  }, [game]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!game) return;

    setLoading(true);
    setError(null);

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      setError("Biztonsági hiba: A CSRF token hiányzik.");
      setLoading(false); // Stop loading animation
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);
    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      const response = await fetch(`/api/games/${game.id}`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      });

      if (response.ok) {
        onSave(); // Refresh game list
        onClose(); // Close modal
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update game.');
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      console.error('Failed to update game:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Játék szerkesztése</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Név
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="col-span-3"
              disabled={loading}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Leírás
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="col-span-3"
              disabled={loading}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="image" className="text-right">
              Kép
            </Label>
            <Input
              id="image"
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
              className="col-span-3"
              disabled={loading}
            />
          </div>
          {error && <p className="text-red-500 text-sm col-span-4 text-center">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Mégse
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Mentés...' : 'Mentés'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditGameModal;